import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { socketManager } from 'utils/socketManager';
import { haveListApi } from 'store/services/collectionList/haveList/haveListApi';
import { wantListApi } from 'store/services/collectionList/wantList/wantListApi';
import { useLazyGetImportJobStatusQuery } from 'store/services/import/importApi';
import {
  importProgress,
  importComplete,
  importSocketConnecting,
  importSocketConnected,
  importSocketDisconnected,
  importSocketError,
  importSocketReconnectAttempt,
} from 'store/slices/importSlice';
import type { ImportProgressPayload, ImportCompletePayload } from 'types/interfaces/import';

interface UseImportSocketOptions {
  enabled?: boolean;
}

interface UseImportSocketReturn {
  isConnected: boolean;
  isConnecting: boolean;
  connectionError: string | null;
}

/**
 * Hook to manage Socket.IO listeners for async import progress events.
 * Uses shared socket manager singleton (same pattern as usePointsSocket).
 *
 * Listens for:
 * - import:progress — Updates live counts in Redux
 * - import:complete — Sets final status, invalidates list cache
 *
 * On reconnection, catches up via GET /imports/{jobId}/status if an import is active.
 */
export const useImportSocket = (
  options: UseImportSocketOptions = {}
): UseImportSocketReturn => {
  const { enabled = true } = options;
  const dispatch = useDispatch();
  const hasAcquiredRef = useRef(false);

  const { isConnected, isConnecting, connectionError } = useSelector(
    (state: RootState) => ({
      isConnected: state.import.isConnected,
      isConnecting: state.import.isConnecting,
      connectionError: state.import.connectionError,
    })
  );

  // Keep a ref to current job state for reconnection catchup
  const jobRef = useRef(useSelector((state: RootState) => state.import.job));
  const jobFromState = useSelector((state: RootState) => state.import.job);
  useEffect(() => {
    jobRef.current = jobFromState;
  }, [jobFromState]);

  const [fetchJobStatus] = useLazyGetImportJobStatusQuery();

  // Handle import:progress event
  const handleImportProgress = useCallback(
    (payload: ImportProgressPayload) => {
      console.log('[ImportSocket] Received import:progress', payload);
      dispatch(importProgress(payload));
    },
    [dispatch]
  );

  // Handle import:complete event
  const handleImportComplete = useCallback(
    (payload: ImportCompletePayload) => {
      console.log('[ImportSocket] Received import:complete', payload);
      dispatch(importComplete(payload));

      // Invalidate the appropriate list cache
      const job = jobRef.current;
      if (job.targetList === 'have') {
        (dispatch as any)(haveListApi.util.invalidateTags(['HaveList']));
      } else if (job.targetList === 'want') {
        (dispatch as any)(wantListApi.util.invalidateTags(['WantList']));
      }
    },
    [dispatch]
  );

  // Reconnection catchup: fetch current status if an import is active
  const handleReconnectionCatchup = useCallback(async () => {
    const job = jobRef.current;
    if (!job.jobId) return;
    if (job.status !== 'pending' && job.status !== 'processing') return;

    console.log('[ImportSocket] Reconnected with active import, catching up via REST');
    try {
      const result = await fetchJobStatus(job.jobId).unwrap();
      if (result.status === 'completed' || result.status === 'failed') {
        dispatch(importComplete({
          jobId: result.jobId,
          status: result.status,
          totalRows: result.totalRows,
          processedRows: result.processedRows,
          matched: result.matched,
          skipped: result.skipped,
          failed: result.failed,
          failedCards: result.failedCards,
          skipReasons: result.skipReasons,
          error: result.error,
        }));
        // Invalidate cache if completed
        if (result.status === 'completed') {
          const currentJob = jobRef.current;
          if (currentJob.targetList === 'have') {
            (dispatch as any)(haveListApi.util.invalidateTags(['HaveList']));
          } else if (currentJob.targetList === 'want') {
            (dispatch as any)(wantListApi.util.invalidateTags(['WantList']));
          }
        }
      } else {
        dispatch(importProgress({
          jobId: result.jobId,
          processedRows: result.processedRows,
          totalRows: result.totalRows,
          matched: result.matched,
          skipped: result.skipped,
          failed: result.failed,
        }));
      }
    } catch (err) {
      console.error('[ImportSocket] Failed to catch up on import status:', err);
    }
  }, [dispatch, fetchJobStatus]);

  // Connection state handler
  const handleConnectionChange = useCallback(
    (connected: boolean) => {
      if (connected) {
        dispatch(importSocketConnected());
      } else {
        dispatch(importSocketDisconnected());
      }
    },
    [dispatch]
  );

  // Main socket effect
  useEffect(() => {
    if (!enabled) {
      return;
    }

    dispatch(importSocketConnecting());
    hasAcquiredRef.current = true;

    const socket = socketManager.acquireSocket();

    // Subscribe to connection state changes
    socketManager.addConnectionListener(handleConnectionChange);

    // Check current connection state
    if (socket.connected) {
      dispatch(importSocketConnected());
    }

    // Connection event handlers
    const onConnect = () => {
      console.log('[ImportSocket] Connected');
      dispatch(importSocketConnected());
      // Catch up on missed progress if an import is active
      handleReconnectionCatchup();
    };

    const onDisconnect = (reason: string) => {
      console.log('[ImportSocket] Disconnected:', reason);
      dispatch(importSocketDisconnected());
    };

    const onConnectError = (error: Error) => {
      console.error('[ImportSocket] Connection error:', error.message);
      dispatch(importSocketError(error.message));
    };

    const onReconnectAttempt = (attemptNumber: number) => {
      console.log('[ImportSocket] Reconnect attempt:', attemptNumber);
      dispatch(importSocketReconnectAttempt());
    };

    const onReconnectFailed = () => {
      console.error('[ImportSocket] Reconnection failed after max attempts');
      dispatch(importSocketError('Reconnection failed'));
    };

    // Register connection event handlers
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('connect_error', onConnectError);
    socket.on('reconnect_attempt', onReconnectAttempt);
    socket.on('reconnect_failed', onReconnectFailed);

    // Import event handlers
    socket.on('import:progress', handleImportProgress);
    socket.on('import:complete', handleImportComplete);

    // Cleanup on unmount
    return () => {
      console.log('[ImportSocket] Cleaning up');
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('connect_error', onConnectError);
      socket.off('reconnect_attempt', onReconnectAttempt);
      socket.off('reconnect_failed', onReconnectFailed);
      socket.off('import:progress', handleImportProgress);
      socket.off('import:complete', handleImportComplete);
      socketManager.removeConnectionListener(handleConnectionChange);

      if (hasAcquiredRef.current) {
        socketManager.releaseSocket();
        hasAcquiredRef.current = false;
      }
    };
  }, [enabled, dispatch, handleImportProgress, handleImportComplete, handleConnectionChange, handleReconnectionCatchup]);

  return {
    isConnected,
    isConnecting,
    connectionError,
  };
};

export default useImportSocket;
